import {compact, entries} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Notation} from 'biojs/BioConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SectionHeader from 'ui-library/menus/SectionHeader';
import sortedBy from 'util/sortedBy';

export interface DeprecatedResidueNotationDropdownProps {
  notation: Notation;
  onSelect: (notation: Notation) => void;
  onSelectCustomSyntax?: (syntaxName: string | null) => void;
  selectedCustomSyntax?: string | null;
  componentName: string;
}

const getValue = (notation: Notation, selectedCustomSyntax?: string | null): string => {
  // To specify a custom syntax requires selecting both Notation.CUSTOM_SYNTAX and a specific syntax name.
  // However we want to present the options as a flattened list to users, and thus need a compound value.
  assert(!selectedCustomSyntax || notation === Notation.CUSTOM_SYNTAX, 'Invalid state');
  return compact([notation, selectedCustomSyntax]).join('-');
};

// This component refers to custom syntaxes by name. We should be using API Ids instead.
export function DeprecatedResidueNotationDropdown({
  notation,
  onSelect,
  onSelectCustomSyntax,
  selectedCustomSyntax,
  componentName,
}: DeprecatedResidueNotationDropdownProps): JSX.Element {
  const showCustomSyntaxOptions =
    onSelectCustomSyntax &&
    BenchlingConfig.MODBIO_ENABLE_CUSTOM_SYNTAXES &&
    BenchlingConfig.MODBIO_CUSTOM_SYNTAX_NAMES.length > 0;

  return (
    <FormGroup>
      <FieldLabel label="Notation" isRequired={true} />
      <SelectDropdown
        displayName={selectedCustomSyntax ?? notation}
        menu={
          <Menu
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
            value={getValue(notation, selectedCustomSyntax)}
          >
            <Option
              key={Notation.NATURAL_BASES}
              value={Notation.NATURAL_BASES}
              onSelect={() => {
                onSelect(Notation.NATURAL_BASES);
                onSelectCustomSyntax?.(null);
              }}
              dataTestAttrs={{
                component: componentName,
                element: 'notationDropdown',
                key: 'bases',
              }}
            >
              Natural bases
            </Option>
            <Option
              key={Notation.HELM}
              value={Notation.HELM}
              onSelect={() => {
                onSelect(Notation.HELM);
                onSelectCustomSyntax?.(null);
              }}
              dataTestAttrs={{
                component: componentName,
                element: 'notationDropdown',
                key: 'helm',
              }}
            >
              HELM
            </Option>
            {BenchlingConfig.MOLBIO_ENABLE_IDT_SYNTAX && (
              <Option
                key={Notation.IDT}
                value={Notation.IDT}
                onSelect={() => {
                  onSelect(Notation.IDT);
                  onSelectCustomSyntax?.(null);
                }}
                dataTestAttrs={{
                  component: componentName,
                  element: 'notationDropdown',
                  key: 'idt',
                }}
              >
                IDT
              </Option>
            )}
            {showCustomSyntaxOptions && <SectionHeader>Custom Notations</SectionHeader>}
            {showCustomSyntaxOptions &&
              BenchlingConfig.MODBIO_CUSTOM_SYNTAX_NAMES.map((name) => (
                <Option
                  key={getValue(Notation.CUSTOM_SYNTAX, name)}
                  value={getValue(Notation.CUSTOM_SYNTAX, name)}
                  onSelect={() => {
                    onSelect(Notation.CUSTOM_SYNTAX);
                    onSelectCustomSyntax(name);
                  }}
                  dataTestAttrs={{
                    component: componentName,
                    element: 'notationDropdown',
                    key: name,
                  }}
                >
                  {name}
                </Option>
              ))}
          </Menu>
        }
      />
    </FormGroup>
  );
}

export interface ResidueNotationDropdownProps {
  notation: Notation;
  onSelectNotation: (notation: Notation) => void;
  onSelectCustomSyntaxId?: (syntaxName: string | null) => void;
  selectedCustomSyntaxId?: string | null;
  componentName: string;
}

export default function ResidueNotationDropdown({
  notation,
  onSelectNotation,
  onSelectCustomSyntaxId,
  selectedCustomSyntaxId,
  componentName,
}: ResidueNotationDropdownProps): JSX.Element {
  const showCustomSyntaxOptions =
    onSelectCustomSyntaxId &&
    BenchlingConfig.MODBIO_ENABLE_CUSTOM_SYNTAXES &&
    Object.keys(BenchlingConfig.MODBIO_CUSTOM_SYNTAX_NAMES_BY_ID).length > 0;

  const selectedCustomSyntaxName = selectedCustomSyntaxId
    ? BenchlingConfig.MODBIO_CUSTOM_SYNTAX_NAMES_BY_ID[selectedCustomSyntaxId]
    : undefined;

  const sortedCustomSyntaxEntries = sortedBy(
    [...entries(BenchlingConfig.MODBIO_CUSTOM_SYNTAX_NAMES_BY_ID)],
    [{accessor: ([, syntaxName]) => syntaxName, naturalSort: true}]
  );
  return (
    <FormGroup>
      <FieldLabel label="Notation" isRequired={true} />
      <SelectDropdown
        displayName={selectedCustomSyntaxName ?? notation}
        menu={
          <Menu
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
            value={getValue(notation, selectedCustomSyntaxId)}
          >
            <Option
              key={Notation.NATURAL_BASES}
              value={Notation.NATURAL_BASES}
              onSelect={() => {
                onSelectNotation(Notation.NATURAL_BASES);
                onSelectCustomSyntaxId?.(null);
              }}
              dataTestAttrs={{
                component: componentName,
                element: 'notationDropdown',
                key: 'bases',
              }}
            >
              Natural bases
            </Option>
            <Option
              key={Notation.HELM}
              value={Notation.HELM}
              onSelect={() => {
                onSelectNotation(Notation.HELM);
                onSelectCustomSyntaxId?.(null);
              }}
              dataTestAttrs={{
                component: componentName,
                element: 'notationDropdown',
                key: 'helm',
              }}
            >
              HELM
            </Option>
            {BenchlingConfig.MOLBIO_ENABLE_IDT_SYNTAX && (
              <Option
                key={Notation.IDT}
                value={Notation.IDT}
                onSelect={() => {
                  onSelectNotation(Notation.IDT);
                  onSelectCustomSyntaxId?.(null);
                }}
                dataTestAttrs={{
                  component: componentName,
                  element: 'notationDropdown',
                  key: 'idt',
                }}
              >
                IDT
              </Option>
            )}
            {showCustomSyntaxOptions && <SectionHeader>Custom Notations</SectionHeader>}
            {showCustomSyntaxOptions &&
              sortedCustomSyntaxEntries.map(([syntaxId, syntaxName]) => (
                <Option
                  key={getValue(Notation.CUSTOM_SYNTAX, syntaxId)}
                  value={getValue(Notation.CUSTOM_SYNTAX, syntaxId)}
                  onSelect={() => {
                    onSelectNotation(Notation.CUSTOM_SYNTAX);
                    onSelectCustomSyntaxId(syntaxId);
                  }}
                  dataTestAttrs={{
                    component: componentName,
                    element: 'notationDropdown',
                    key: syntaxName,
                  }}
                >
                  {syntaxName}
                </Option>
              ))}
          </Menu>
        }
      />
    </FormGroup>
  );
}
